import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DataGrid } from '@mui/x-data-grid';
import { ptBR } from '@mui/x-data-grid/locales';
import * as React from 'react';
import Copyright from '../../internals/components/Copyright';

const statusColors = {
  pending: 'warning',
  processing: 'info',
  finished: 'success',
  error: 'error',
};

const getStatusLabel = (state) => {
  switch (state) {
    case 'pending': return 'Pendente';
    case 'processing': return 'Processando';
    case 'finished': return 'Finalizado';
    case 'error': return 'Erro';
    default: return state;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('pt-BR');
};

const handleDownload = (downloadUrl) => {
  if (downloadUrl) {
    window.open(downloadUrl, '_blank');
  }
};

export const columns = [
  {
    field: 'day',
    headerName: 'Data',
    flex: 1,
    minWidth: 120,
    renderCell: (params) => formatDate(params.value)
  },
  {
    field: 'state',
    headerName: 'Status',
    flex: 1,
    minWidth: 140,
    renderCell: (params) => {
      return (
        <Chip
          label={getStatusLabel(params.value)}
          color={statusColors[params.value]}
          size="small"
        />
      );
    }
  },
  {
    field: 'spreadsheet_filename',
    headerName: 'Planilha',
    flex: 2,
    minWidth: 200,
    renderCell: (params) => params.value || '-'
  },
  {
    field: 'imported_lines',
    headerName: 'Linhas Importadas',
    flex: 1,
    minWidth: 160,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => params.value || 0
  },
  {
    field: 'invalid_lines',
    headerName: 'Linhas Inválidas',
    flex: 1,
    minWidth: 160,
    align: 'center',
    headerAlign: 'center',
    renderHeader: () => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        Linhas Inválidas
        <Tooltip title="Total de linhas onde o nome do motorista estava em branco e não pôde ser importado.">
          <HelpOutlineIcon fontSize="small" color="action" />
        </Tooltip>
      </Box>
    ),
    renderCell: (params) => params.value || 0
  },
  {
    field: 'created_at',
    headerName: 'Importado Em',
    flex: 1.5,
    minWidth: 180,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary">
          {formatDateTime(params.value)}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'download_url',
    headerName: 'Ações',
    flex: 1,
    minWidth: 100,
    align: 'center',
    headerAlign: 'center',
    sortable: false,
    filterable: false,
    renderCell: (params) => {
      return (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 0.5
        }}>
          <Tooltip title="Download da planilha">
            <IconButton
              size="small"
              onClick={() => handleDownload(params.value)}
              color="primary"
              sx={{
                height: '30px',
                width: '35px',
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }
  }
];

export default function DataStreamsTable({ data_streams = [] }) {
  if (data_streams.length === 0) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Nenhuma planilha importada
          </Typography>
          <Typography variant="body2">
            Comece importando sua primeira planilha clicando no botão "Importar Planilha" acima.
          </Typography>
        </Alert>
        <Copyright sx={{ my: 4 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Planilhas Importadas
      </Typography>

      <Box sx={{ width: '100%' }}>
        <DataGrid
          localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
          rows={data_streams}
          columns={columns}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 20, 50, 100, 200, 500]}
          disableColumnResize
          density="compact"
          slotProps={{
            filterPanel: {
              filterFormProps: {
                logicOperatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                },
                columnInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                operatorInputProps: {
                  variant: 'outlined',
                  size: 'small',
                  sx: { mt: 'auto' },
                },
                valueInputProps: {
                  InputComponentProps: {
                    variant: 'outlined',
                    size: 'small',
                  },
                },
              },
            },
          }}
        />
      </Box>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
} 